import { clamp, createRng, loopNoise } from './math'

export type Vec3 = [number, number, number]

export const EYE_SPLIT = 15.46
export const EYE_W = 0.186
export const EYE_H = 0.412

export const REST_GAZE: HeadGaze = { yaw: 28.49, pitch: 28.62, roll: -13 }

export interface EyePose {
  x: number
  y: number
  a: number
  b: number
  c: number
  d: number
  depth: number
}

export interface HeadGaze {
  yaw: number
  pitch: number
  roll: number
}

const deg = (d: number) => (d * Math.PI) / 180

function spin(u: Vec3, v: Vec3, angle: number): [Vec3, Vec3] {
  const c = Math.cos(angle)
  const s = Math.sin(angle)
  return [
    [u[0] * c + v[0] * s, u[1] * c + v[1] * s, u[2] * c + v[2] * s],
    [v[0] * c - u[0] * s, v[1] * c - u[1] * s, v[2] * c - u[2] * s]
  ]
}

/** Head axes after the same yaw → pitch → roll the eyes use. */
export function gazeAxes(gaze: HeadGaze): { f: Vec3; right: Vec3; down: Vec3 } {
  let f: Vec3 = [0, 0, 1]
  let right: Vec3 = [1, 0, 0]
  let down: Vec3 = [0, 1, 0]

  ;[f, right] = spin(f, right, deg(gaze.yaw))
  ;[down, f] = spin(down, f, deg(gaze.pitch))
  ;[right, down] = spin(right, down, deg(gaze.roll))
  return { f, right, down }
}

/** Local (right, down, forward) → world after `gaze`. */
export function mapByGaze(p: Vec3, gaze: HeadGaze): Vec3 {
  const { f, right, down } = gazeAxes(gaze)
  return [
    p[0] * right[0] + p[1] * down[0] + p[2] * f[0],
    p[0] * right[1] + p[1] * down[1] + p[2] * f[1],
    p[0] * right[2] + p[1] * down[2] + p[2] * f[2]
  ]
}

/** Inverse of `mapByGaze` — the basis is orthonormal. */
export function unmapByGaze(p: Vec3, gaze: HeadGaze): Vec3 {
  const { f, right, down } = gazeAxes(gaze)
  return [
    p[0] * right[0] + p[1] * right[1] + p[2] * right[2],
    p[0] * down[0] + p[1] * down[1] + p[2] * down[2],
    p[0] * f[0] + p[1] * f[1] + p[2] * f[2]
  ]
}

/**
 * Rest-pose screen point (ball units) seated on the eye sphere and turned from
 * `from` to `gaze`. Identity when the two gazes match. Points inside the unit
 * disc lift onto the sphere so they rotate like the eyes; lobe tips beyond
 * r = 1 stay equatorial.
 */
export function seatOnGaze(
  x: number,
  y: number,
  gaze: HeadGaze,
  from: HeadGaze = REST_GAZE
): Vec3 {
  const rr = x * x + y * y
  const z = rr < 1 ? Math.sqrt(1 - rr) : 0
  return mapByGaze(unmapByGaze([x, y, z], from), gaze)
}

export function eyePoses(gaze: HeadGaze, scale: number, split = EYE_SPLIT): [EyePose, EyePose] {
  const { f, right, down } = gazeAxes(gaze)

  const build = (side: number): EyePose => {
    const [ef, er] = spin(f, right, deg(split * side))
    return {
      x: ef[0] * scale,
      y: ef[1] * scale,
      a: er[0],
      b: er[1],
      c: down[0],
      d: down[1],
      depth: ef[2]
    }
  }

  return [build(-1), build(1)]
}

export interface Liveliness {
  dYaw: number
  dPitch: number
  dRoll: number
  lids: [number, number]
  driftX: number
  driftY: number
  breath: number
}

const BLINK_RNG = createRng(0x5eed)
const LID_LAG = 0.022

export const BLINK_DUR = 0.18

interface BlinkEvt {
  at: number
  dur: number
  lag: number
}

const BLINK_EVENTS: BlinkEvt[] = (() => {
  const out: BlinkEvt[] = []
  let t = 1.4
  while (t < 900) {
    const dur = BLINK_DUR * (0.88 + BLINK_RNG() * 0.28)
    out.push({ at: t, dur, lag: BLINK_RNG() < 0.35 ? LID_LAG : 0 })
    t += 1.7 + BLINK_RNG() * 2.5
    if (BLINK_RNG() < 0.22) {
      out.push({ at: t, dur: dur * 0.85, lag: LID_LAG * 0.7 })
      t += 0.18 + BLINK_RNG() * 0.12
    }
    if (BLINK_RNG() < 0.06) {
      out.push({ at: t, dur: dur * 0.75, lag: LID_LAG })
      t += 0.16
    }
  }
  return out
})()

function singleBlinkLid(t: number, start: number, dur: number): number {
  const k = (t - start) / dur
  if (k < 0 || k > 1) return 1
  return k < 0.42 ? 1 - k / 0.42 : (k - 0.42) / 0.58
}

function blinkLids(t: number): [number, number] {
  let left = 1
  let right = 1
  for (const e of BLINK_EVENTS) {
    if (t < e.at) break
    left = Math.min(left, singleBlinkLid(t, e.at, e.dur))
    right = Math.min(right, singleBlinkLid(t, e.at + e.lag, e.dur))
  }
  return [left, right]
}

const SACC_RNG = createRng(0x5acc)
const SACCADES: number[] = (() => {
  const out: number[] = []
  let t = 2.1
  while (t < 900) {
    out.push(t)
    t += 1.6 + SACC_RNG() * 2.2
  }
  return out
})()

export function saccadeOffset(t: number, wander: number): { dYaw: number; dPitch: number } {
  if (!wander) return { dYaw: 0, dPitch: 0 }
  for (let i = 0; i < SACCADES.length; i++) {
    const start = SACCADES[i]!
    const k = (t - start) / 0.085
    if (k >= 0 && k <= 1) {
      const burst = Math.sin(k * Math.PI)
      const seed = i * 7.31
      return {
        dYaw: burst * (loopNoise(start, 1.0, seed) * 2.2 + loopNoise(start, 0.65, seed + 1) * 1.1),
        dPitch: burst * loopNoise(start, 1.15, seed + 2) * 1.7
      }
    }
    if (t < start) break
  }
  return { dYaw: 0, dPitch: 0 }
}

export interface LivelinessOptions {
  wander?: number
  blink?: boolean
  float?: boolean
  saccade?: boolean
}

export function liveliness(t: number, opt: LivelinessOptions = {}): Liveliness {
  const { wander = 1, blink = true, float = true, saccade = true } = opt
  const sac = saccadeOffset(t, wander && saccade ? 1 : 0)

  return {
    dYaw:
      (loopNoise(t, 11.3, 0.4) * 5.5 + loopNoise(t, 3.7, 2.1) * 1.6 + sac.dYaw) * wander,
    dPitch:
      (loopNoise(t, 9.1, 1.3) * 4.2 + loopNoise(t, 4.3, 0.7) * 1.3 + sac.dPitch) * wander,
    dRoll: (loopNoise(t, 13.7, 3.2) * 2.2 + loopNoise(t, 6.1, 1.7) * 0.55) * wander,
    lids: blink ? blinkLids(t) : [1, 1],
    driftX: float
      ? loopNoise(t, 7.9, 1.9) * 0.006 + loopNoise(t, 3.2, 4.1) * 0.001
      : 0,
    driftY: float
      ? loopNoise(t, 5.3, 0.3) * 0.007 + loopNoise(t, 2.8, 2.6) * 0.0012
      : 0,
    breath: float
      ? 1 +
        Math.sin((t / 3.4) * Math.PI * 2) * 0.005 +
        Math.sin((t / 5.7) * Math.PI * 2) * 0.002
      : 1
  }
}

export function blinkScale(lid: number): number {
  return 0.06 + 0.94 * clamp(lid)
}
